import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useDeleteNotifMutation, useReadNotifMutation, useUnreadNotifMutation } from '@/resolvers/user/mutation'
import { useMyNotificationsContext } from '@/context/MyNotificationsContext'

/**
 * Custom hook that provides layout for notifications.
 * It uses several other hooks and functions to handle various aspects of the layout.
 *
 * @returns {Object} The following properties are returned:
 *
 * @prop {boolean} markPending - Indicates if the mark as read mutation is currently pending.
 * @prop {boolean} isActionDisabled - Indicates if the action button should be disabled (based on whether any notifications are checked).
 * @prop {function} handleMark - Function to trigger the mark as read mutation.
 * @prop {function} handleUnread - Function to trigger the mark as unread mutation.
 * @prop {function} t - Function from useTranslation hook to provide localization.
 * @prop {Array} notifications - Array of notifications from the useMyNotificationsContext hook.
 * @prop {number} page - The current page number.
 * @prop {function} onPageChange - Function to handle page number changes.
 * @prop {function} handleDeleteSelected - Function to trigger the delete selected notifications mutation.
 */
const useNotificationsLayout = () => {
  const { t } = useTranslation()

  const { notifications, checks, setChecks, page, setPage, handleList } = useMyNotificationsContext()
  const { mutate: markReadMutation, isPending: markPending } = useReadNotifMutation()
  const { mutate: unreadMutation } = useUnreadNotifMutation()
  const { mutate: deleteMutation } = useDeleteNotifMutation()

  const onPageChange = useCallback(
    (_f: number, _t: number, newPage: number) => {
      if (page !== newPage) {
        setChecks([])
        setPage(newPage)
      }
    },
    [setChecks, page, setPage]
  )

  const isActionDisabled = checks.length < 1

  useEffect(() => {
    handleList()
  }, [handleList])

  return {
    markPending,
    isActionDisabled,
    handleMark: () => markReadMutation(),
    handleUnread: () => unreadMutation(),
    t,
    notifications,
    page,
    onPageChange,
    handleDeleteSelected: () => deleteMutation(),
  }
}

export default useNotificationsLayout
